import config from '../config/environment';
import supabase from '../config/supabase';
import { handleServiceError } from '../utils/errorHandler';
import { withRequestIdHeaders } from '../utils/requestId';
import { mapProjectIdentity } from '../utils/project';
import type { CheckoutSelectedPlan } from '../pages/Checkout/checkout.types';
import {
  getChargeCheckoutSuccessMessage,
  parseCheckoutPaymentInstructions,
} from '../pages/Checkout/checkoutPaymentInstructions.utils';
import { CHECKOUT_PROCESSING_ERROR_MESSAGE } from '../pages/Checkout/checkout.constants';
import type { CheckoutPaymentResult } from '../pages/Checkout/checkout.types';

interface ChargeCheckoutEdgeResponse<T> {
  success?: boolean;
  data?: T;
  error?: {
    code?: string;
    message?: string;
  } | string;
}

export interface GenerateChargeCheckoutLinkResult {
  token: string;
  expiresAt: string;
  reusedExisting: boolean;
  studentId: number;
  studentName: string;
  planId: number;
  paymentAccountId: number;
  externalCustomerId: string;
  planHash: string;
  mode?: 'new_charge' | 'pay_existing' | 'charge_checkout';
  paymentHistoryId?: number | null;
  externalPaymentId?: string | null;
  openPaymentValue?: number | null;
  openPaymentDueDate?: string | null;
}

export interface PreviewChargeCheckoutResult {
  mode: 'charge_checkout' | 'pay_existing';
  chargeToken: string;
  studentId: number;
  studentName: string;
  selectedPlan: CheckoutSelectedPlan;
  paymentAccountId: number;
  externalCustomerId: string;
  openPayment?: {
    paymentHistoryId: number;
    externalPaymentId: string;
    value: number;
    currency: string | null;
    status: string;
    dueDate: string | null;
  } | null;
}

export interface ChargeCheckoutSubmissionPayload {
  chargeToken: string;
  selectedPlan: { hash: string };
  paymentMethod: 'pix' | 'boleto' | 'credit_card';
  creditCard?: {
    holderName: string;
    number: string;
    expiryMonth: string;
    expiryYear: string;
    cvv: string;
  };
  creditCardHolderInfo?: {
    name: string;
    email: string;
    cpfCnpj: string;
    postalCode: string;
    addressNumber: string;
    addressComplement: string | null;
    phone: string;
    mobilePhone: string;
  };
}

function resolveErrorMessage(body: ChargeCheckoutEdgeResponse<unknown>): string {
  if (typeof body.error === 'string' && body.error.trim()) {
    return body.error.trim();
  }

  if (body.error && typeof body.error === 'object' && body.error.message?.trim()) {
    return body.error.message.trim();
  }

  return CHECKOUT_PROCESSING_ERROR_MESSAGE;
}

class ChargeCheckoutServiceClass {
  async generateLink(studentId: number, planId: number): Promise<GenerateChargeCheckoutLinkResult> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token?.trim();

    if (!token) {
      handleServiceError(new Error('Usuária não autenticada.'), 'gerar link de pagamento');
    }

    const response = await fetch(`${config.supabaseFunctionsUrl}/students`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
      body: JSON.stringify({
        action: 'generate-charge-checkout-link',
        payload: { studentId, planId },
      }),
    });

    const body = await response.json().catch(() => ({})) as ChargeCheckoutEdgeResponse<GenerateChargeCheckoutLinkResult>;

    if (!response.ok || body.success === false || !body.data?.token) {
      handleServiceError(
        new Error(resolveErrorMessage(body)),
        'gerar link de pagamento',
      );
    }

    return body.data!;
  }

  async preview(chargeToken: string, planHash: string): Promise<PreviewChargeCheckoutResult> {
    const response = await fetch(`${config.supabaseFunctionsUrl}/students`, {
      method: 'POST',
      headers: withRequestIdHeaders({
        'Content-Type': 'application/json',
        apikey: config.supabaseAnonKey,
        Authorization: `Bearer ${config.supabaseAnonKey}`,
      }),
      body: JSON.stringify({
        action: 'preview-charge-checkout',
        payload: {
          chargeToken,
          selectedPlan: { hash: planHash },
        },
      }),
    });

    const body = await response.json().catch(() => ({})) as ChargeCheckoutEdgeResponse<PreviewChargeCheckoutResult>;

    if (!response.ok || body.success === false || !body.data?.selectedPlan) {
      throw new Error(resolveErrorMessage(body));
    }

    return {
      ...body.data,
      selectedPlan: {
        ...body.data.selectedPlan,
        project: mapProjectIdentity(body.data.selectedPlan.project),
      },
    };
  }

  async processCharge(payload: ChargeCheckoutSubmissionPayload): Promise<CheckoutPaymentResult> {
    const response = await fetch(`${config.supabaseFunctionsUrl}/students`, {
      method: 'POST',
      headers: withRequestIdHeaders({
        'Content-Type': 'application/json',
        apikey: config.supabaseAnonKey,
        Authorization: `Bearer ${config.supabaseAnonKey}`,
      }),
      body: JSON.stringify({
        action: 'charge-checkout',
        payload,
      }),
    });

    const body = await response.json().catch(() => ({})) as ChargeCheckoutEdgeResponse<{
      studentId?: number;
      payment?: {
        subscription?: { id?: string | null };
        checkoutInstructions?: unknown;
        initialPayment?: { id?: string | null } | null;
      };
    }>;

    if (!response.ok || body.success === false) {
      return {
        success: false,
        message: resolveErrorMessage(body),
        continuation: null,
      };
    }

    const paymentInstructions = parseCheckoutPaymentInstructions(
      body.data?.payment?.checkoutInstructions,
    );
    const initialPaymentId = body.data?.payment?.initialPayment?.id;
    const externalPaymentId = (
      paymentInstructions?.externalPaymentId
      || (typeof initialPaymentId === 'string' ? initialPaymentId.trim() : '')
      || null
    );

    return {
      success: true,
      message: getChargeCheckoutSuccessMessage(paymentInstructions, false, payload.paymentMethod),
      externalReference: body.data?.payment?.subscription?.id ?? `charge-${body.data?.studentId ?? Date.now()}`,
      externalPaymentId,
      paymentInstructions,
    };
  }
}

export const ChargeCheckoutService = new ChargeCheckoutServiceClass();
